import React from 'react';

import i18n from 'util/i18n';

import { getYearsString } from 'helpers/text';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { ReasonLeftModel } from 'forms/sections/history/employment/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import BranchCollection from 'components/Form/BranchCollection';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import ReasonOptions from './ReasonOptions';
import { extractDate } from '../dateranges';

interface Props extends ValidationElementProps, Partial<ReasonLeftModel> {
  onUpdate?: CommonOnUpdate
  recordYears?: number
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  attribute?: string
  formType?: string
}

export default class ReasonLeft extends ValidationElement<Props> {
  /**
   * Handle any updates and bubble them up.
   */
  update = (queue: Partial<ReasonLeftModel>) => {
    const allowDescription = this.showDescription();
    const allowOptions = this.showOptions();
    this.props.onUpdate({
      name: this.props.name,
      Reasons: allowOptions ? this.props.Reasons : { items: [], branch: {} },
      ReasonDescription: allowDescription ? this.props.ReasonDescription : {},
      Comments: allowDescription ? this.props.Comments : {},
      ...queue,
    });
  };

  updateReasonDescription = (values: ReasonLeftModel['ReasonDescription']) => {
    this.update({ ReasonDescription: values });
  };

  updateComments = (values: ReasonLeftModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateReasons = (values: ReasonLeftModel['Reasons']) => {
    this.update({ Reasons: values });
  };

  /**
   * Only show the reasons for leaving if
   *  - not currently employed at position
   */
  showDescription = () => this.props.Dates?.present !== true ?? false;

  /**
   * Only show the reasons options if:
   *  - not currently employed at position AND
   *  - employed at position within the last # years
   */
  showOptions = (years = 7) => {
    const now = new Date();
    const recordYearsAgo = new Date(now.getFullYear() - years, now.getMonth(), 1);

    const from = extractDate(this.props.Dates?.from);
    const to = this.props.Dates?.present === true ? now : extractDate(this.props.Dates?.to);

    return (
      this.props.Dates?.present !== true
      && ((from && from >= recordYearsAgo) || (to && to >= recordYearsAgo))
    );
  };

  branchComment = () => (
    <Comments
      title={i18n.t(`history.employment.label.reasonLeftAnswerComments`)}
      name="ReasonLeftAnswerComments"
      attribute="ReasonLeftAnswerComments"
      className="optional-comments"
      {...this.props.ReasonLeftAnswerComments}
      onUpdate={this.props.updateReasonLeftAnswerComments}
      onError={this.props.onError}
      required={false}
    />
  );

  render() {
    const { recordYears } = this.props;
    const recordYearsString = getYearsString(recordYears);
    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div className="reason-leaving">
        <Show when={this.showDescription()}>
          <Field
            attribute={setAttribute(this.props.attribute, 'ReasonDescription')}
            title={i18n.t('history.pvq.employment.default.left.title')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={setAttribute(this.props.attribute, 'ReasonDescription')}
              name="reason_description"
              {...this.props.ReasonDescription}
              className="reason-description"
              onUpdate={this.updateReasonDescription}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>
      </div>
    ) : (
      <div className="reason-leaving">
        <Show when={this.showDescription()}>
          <Field
            attribute={setAttribute(this.props.attribute, 'ReasonDescription')}
            title={i18n.t('history.employment.default.left.title')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={setAttribute(this.props.attribute, 'ReasonDescription')}
              name="reason_description"
              {...this.props.ReasonDescription}
              className="reason-description"
              onUpdate={this.updateReasonDescription}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          <Comments
            title={i18n.t('history.employment.default.left.optionalComments')}
            name="Comments"
            className="optional-comments"
            attribute="Comments"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        </Show>

        <Show when={this.showOptions(recordYears)}>
          <BranchCollection
            attribute={setAttribute(this.props.attribute, 'Reasons')}
            label={i18n.t('history.employment.default.left.branch', {
              years: recordYears,
              yearsString: recordYearsString,
            })}
            appendLabel={i18n.t('history.employment.default.left.append', {
              years: recordYears,
              yearsString: recordYearsString,
            })}
            content={i18n.m('history.employment.default.left.list')}
            {...(this.props.Reasons || {})}
            className="reason-options"
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            branchComment={this.branchComment}
          >
            <ReasonOptions
              attribute={setAttribute(this.props.attribute, 'Reasons')}
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              Dates={this.props.Dates}
              {...this.props.Reasons}
            />
          </BranchCollection>
        </Show>
        {this.props.Reasons.items?.[0]?.Item?.Has?.value === 'Yes' && (
        <Comments
          title={i18n.t('history.employment.default.left.reasonLeftComments')}
          name="ReasonLeftComments"
          className="optional-comments"
          attribute="ReasonLeftComments"
          {...this.props.ReasonLeftComments}
          onUpdate={this.props.updateReasonLeftComments}
          required={false}
        />
        )}
      </div>
    );
  }
}

ReasonLeft.defaultProps = {
  Comments: {},
  ReasonLeftComments: {},
  ReasonLeftAnswerComments: {},
  ReasonDescription: {},
  Reasons: {},
  Dates: {},
  formType: '',
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
